import {
  UserOutlined,
  TeamOutlined,
  CrownOutlined,
  UsergroupAddOutlined,
  FileTextOutlined,
  BulbOutlined,
  FormOutlined,
  ClusterOutlined,
  MessageOutlined,
  EllipsisOutlined,
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import {
  Collapse,
  Input,
  Typography,
  Select,
  Alert,
  Col,
  Row,
  Divider,
  Modal,
  notification,
  message,
} from 'antd';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import useAPIQuery from '../../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../../api/useInfiniteAPIQuery';
import AdditionalQuestionsForm, {
  AdditionalQuestionsFormValues,
} from '../../../components/AdditionalQuestionsForm';
import EmailForm from '../../../components/EmailForm';
import Form from '../../../components/Form';
import TestsForm from '../../../components/TestsForm';
import useDiscardChangesModal from '../../../hooks/useDiscardChangesModal';
import usePrefetchPages from '../../../hooks/usePrefetchPages';
import { t as _t } from '../../../i18n';
import { SinapseScreeningQuestionsResult } from '../../../sinapse/api';
import { ACTION_GENERATE_SCREENING_QUESTIONS } from '../../../sinapse/constants/actions';
import { ScreeningQuestionsGenerator } from '../../../sinapse/generate_screening_questions';
import useAsyncSinapseResponse from '../../../sinapse/hooks/useAsyncSinapseResponse';
import {
  ExpandedInterviewScriptTemplateCollection,
  JobPostingDetail,
  StageSubTypeChoices,
  StageSubTypeKeys,
} from '../../../types';
import { getFrontendManualTranslations } from '../../../utils';
import InterviewStageForm from './InterviewStageForm';
import JobPostingRegistrationTemplateForm from './JobPostingRegistrationTemplateForm';
import PracticalTechnicalCaseForm from './PracticalTechnicalCaseForm';
import styles from './StagesForm.module.css';

interface StageFormItemProps {
  fixedName?: boolean;
  additionalQuestionsForm?: boolean;
  registrationTemplateForm?: boolean;
  namePrefix?: (string | number)[];
  templateForm?: boolean;
  tests?: string[];
  jobPostingId?: JobPostingDetail['id'];
  interviewScriptTemplates: ExpandedInterviewScriptTemplateCollection[];
  isGeneratingInterviewScriptState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  setIsDirty: ReturnType<typeof useDiscardChangesModal>[0];
  isDescriptionEmpty: boolean;
  enableChangeLanguage: boolean;
}

const StageFormItem = ({
  namePrefix = [],
  fixedName,
  templateForm,
  tests,
  jobPostingId,
  additionalQuestionsForm,
  registrationTemplateForm,
  interviewScriptTemplates,
  isGeneratingInterviewScriptState,
  setIsDirty,
  isDescriptionEmpty = true,
  enableChangeLanguage,
}: StageFormItemProps) => {
  const { t } = useTranslation();

  const form = Form.useFormInstance();
  const [isGeneratingInterviewScript] = isGeneratingInterviewScriptState;

  const config = useAPIQuery('/configs/tenant-configurations/');

  const emailCommunicationTemplatesPages = useInfiniteAPIQuery([
    '/email-communication-templates/',
    {
      params: {
        type: 'TESTS',
        individual: false,
      },
    },
  ]);

  const emailCommunicationTemplates = usePrefetchPages(
    emailCommunicationTemplatesPages,
    (data) => data.results
  );

  const recruitmentProcesses = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          job_posting: jobPostingId,
        },
      },
    ],
    { enabled: additionalQuestionsForm && !!jobPostingId }
  );

  const [disabledInput, setIsDisabledInput] = useState<boolean>(false);

  const { responseStatus, processResult } =
    useAsyncSinapseResponse<SinapseScreeningQuestionsResult>({
      setIsDisabledInput,
      setResult: (result) => {
        if (!result.questions) {
          return notification.error({
            message: t('Erro ao gerar as perguntas de pré-requisito'),
            description: result.text,
          });
        }

        form.setFieldValue(
          ['stages', ...namePrefix, 'questions'],
          result.questions.map(({ question, options }) => ({
            description: question,
            required: false,
            type: 'RADIO',
            options: options.map((option) => ({ description: option })),
          }))
        );

        message.success(t('Perguntas geradas com sucesso'));
      },
      sourceId: jobPostingId,
      action: ACTION_GENERATE_SCREENING_QUESTIONS,
      configs: {
        overwriteOnSuccess: true,
        queryKeyPath:
          jobPostingId && additionalQuestionsForm
            ? ['sinapse', 'screeningQuestions', jobPostingId]
            : undefined,
      },
      enabled: config.data?.enableGenerateScreeningQuestions && additionalQuestionsForm,
    });

  const loadingAIResponse = responseStatus === 'pending' && disabledInput;

  const confirmAdditionalQuestionsOverride = useCallback(
    () =>
      Modal.confirm({
        title: _t('Sobrescrever as perguntas atuais?'),
        content: _t(
          'Ao gerar as perguntas de pré-requisito, as perguntas atuais serão substituídas. Você tem certeza que deseja prosseguir?'
        ),
        onOk: () => processResult?.(),
        okText: _t('Sim'),
        cancelText: _t('Não'),
        okButtonProps: {
          icon: <CheckOutlined />,
        },
        cancelButtonProps: { icon: <CloseOutlined />, danger: true },
      }),
    [processResult]
  );

  const nameValue = Form.useWatch([...namePrefix, 'name'], form);
  const subType: StageSubTypeKeys = Form.useWatch([...namePrefix, 'sub_type'], form);
  const testTypeListValue = Form.useWatch([...namePrefix, 'testTypeList'], form);
  const messageValue = Form.useWatch([...namePrefix, 'message'], form);
  const subjectValue = Form.useWatch([...namePrefix, 'subject'], form);
  const fromNameValue = Form.useWatch([...namePrefix, 'fromName'], form);
  const attachmentsValue = Form.useWatch([...namePrefix, 'attachments'], form);
  const questionsValue = Form.useWatch<AdditionalQuestionsFormValues['questions']>(
    [...namePrefix, 'questions'],
    form
  );
  const lockInterviewScriptValue = Form.useWatch([...namePrefix, 'lock_interview_script'], form);

  const disableAdditionalQuestions = (recruitmentProcesses.data?.count ?? 0) > 0;

  return (
    <>
      {fixedName ? (
        <Typography.Title level={3}>{nameValue}</Typography.Title>
      ) : (
        <>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name={[...namePrefix, 'sub_type']}
                label={t('Tipo da etapa')}
                rules={[{ required: true, message: t('Selecione um tipo de etapa') }]}
              >
                <Select
                  allowClear={false}
                  showSearch
                  disabled={isGeneratingInterviewScript || lockInterviewScriptValue}
                  options={Object.entries(StageSubTypeChoices)
                    .filter(([key]) => key !== 'LEGACY')
                    .map(([key, label]) => ({
                      value: key,
                      label: (
                        <>
                          {stageSubTypeIconMap[key as keyof typeof stageSubTypeIconMap]}{' '}
                          {getFrontendManualTranslations(label, enableChangeLanguage)}
                        </>
                      ),
                    }))
                    .filter(
                      ({ value }) =>
                        value !== 'SCREENING_INTERVIEW' || subType === 'SCREENING_INTERVIEW'
                    )}
                  onChange={(value) => {
                    if (
                      nameValue === '' ||
                      nameValue === t('Nova etapa') ||
                      Object.values(StageSubTypeChoices).includes(nameValue)
                    ) {
                      if (value === 'OTHERS' || value === 'COMMUNICATION') {
                        form.setFieldValue(['stages', ...namePrefix, 'name'], '');
                        return;
                      }
                      form.setFieldValue(
                        ['stages', ...namePrefix, 'name'],
                        getFrontendManualTranslations(
                          StageSubTypeChoices[value as StageSubTypeKeys],
                          enableChangeLanguage
                        )
                      );
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={[...namePrefix, 'name']}
                label={t('Nome da etapa')}
                rules={[{ required: true }]}
              >
                <Input disabled={isGeneratingInterviewScript} />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          {[
            'SCREENING_INTERVIEW',
            'HR_INTERVIEW',
            'MANAGER_INTERVIEW',
            'STAKEHOLDERS_INTERVIEW',
          ].includes(subType) && (
            <InterviewStageForm
              stageSubType={subType}
              isGeneratingInterviewScriptState={isGeneratingInterviewScriptState}
              interviewScriptTemplates={interviewScriptTemplates}
              setIsDirty={setIsDirty}
              jobPostingId={jobPostingId}
              generateInterviewScript={config.data?.enableGenerateInterviewScript}
              isDescriptionEmpty={isDescriptionEmpty}
              namePrefix={namePrefix}
            />
          )}
          {subType === 'PRACTICAL_TECHNICAL_CASE' && (
            <PracticalTechnicalCaseForm
              namePrefix={namePrefix}
              isGeneratingInterviewScriptState={isGeneratingInterviewScriptState}
              interviewScriptTemplates={interviewScriptTemplates}
              generatePracticalTechnicalCase={config.data?.enableGeneratePracticalTechnicalCase}
              setIsDirty={setIsDirty}
              jobPostingId={jobPostingId}
              isDescriptionEmpty={isDescriptionEmpty}
            />
          )}
        </>
      )}
      {subType === 'MINDSIGHT_TESTS' && (
        <Alert
          data-testid="mindsight-tests-alert"
          message={
            <p>
              <Trans>
                Recomendamos aplicar os testes da Mindsight na <strong>etapa de inscrição</strong>,
                já que eles foram desenvolvido para otimizar a triagem e aumentar a predição de
                performance logo no início do processo. Se forem utilizados nesta etapa, o envio do
                email com o link dos testes deverá ser feito manualmente após mover os candidatos.
              </Trans>
            </p>
          }
          type="info"
          showIcon
        />
      )}
      {subType === 'CUSTOM_TEST' && (
        <Alert
          data-testid="custom-tests-alert"
          message={
            <p>
              <Trans>
                Para configurar um teste customizado nesta etapa, entre em contato com o CS
                responsável pelo seu atendimento. Saiba mais sobre essa funcionalidade clicando
              </Trans>{' '}
              <a
                href="https://suporte.mindsight.com.br/testes-customizados-como-funciona"
                target="_blank"
              >
                <Trans>aqui</Trans>
              </a>
              .
            </p>
          }
          type="info"
          showIcon
        />
      )}
      <Collapse
        ghost
        defaultActiveKey={[
          ...(registrationTemplateForm ? ['registrationTemplate'] : []),
          'additionalQuestions',
          'sendTests',
        ]}
        className={styles.collapse}
      >
        {registrationTemplateForm && config.data?.t009_subscriptionTemplates && (
          <Collapse.Panel
            header={
              <Typography.Text strong>
                <Trans>Configuração de formulário de inscrição</Trans>
              </Typography.Text>
            }
            key="registrationTemplate"
          >
            <JobPostingRegistrationTemplateForm />
          </Collapse.Panel>
        )}
        {additionalQuestionsForm && (
          <Collapse.Panel
            header={
              <Typography.Text strong>
                <Trans>Validação de pré-requisitos da vaga</Trans>
              </Typography.Text>
            }
            key="additionalQuestions"
          >
            <AdditionalQuestionsForm
              namePrefix={namePrefix}
              disabled={disableAdditionalQuestions || loadingAIResponse}
              disabledInfo={disableAdditionalQuestions}
              aiAction={
                <>
                  {config.data?.enableGenerateScreeningQuestions && (
                    <ScreeningQuestionsGenerator
                      loading={loadingAIResponse}
                      disabled={disableAdditionalQuestions || isDescriptionEmpty}
                      disabledMessage={
                        isDescriptionEmpty
                          ? t('Preencha a descrição da vaga para gerar as perguntas')
                          : ''
                      }
                      onGenerateClick={() => {
                        setIsDirty(true);

                        if (questionsValue && questionsValue.length > 0) {
                          return confirmAdditionalQuestionsOverride();
                        }

                        processResult?.();
                      }}
                    />
                  )}
                </>
              }
            />
          </Collapse.Panel>
        )}
        {templateForm && (
          <Collapse.Panel
            header={
              <Typography.Text strong>
                <Trans>Configuração de envio de testes</Trans>
              </Typography.Text>
            }
            key="sendTests"
          >
            <Typography.Paragraph>
              <Trans>
                Configure um email automático para que a pessoa candidata receba os links dos testes
                após finalizar a inscrição:
              </Trans>
            </Typography.Paragraph>
            <TestsForm
              namePrefix={namePrefix}
              tests={tests || []}
              showNexa={config.data?.enableNexaAssessment}
            />
            <EmailForm
              namePrefix={namePrefix}
              fromName
              image
              staticSubject={false}
              attachments
              customVariables={[
                { value: '[link_testes]', label: t('Link dos Testes') },
                { value: '[candidato]', label: t('Nome do Candidato') },
                { value: '[empresa]', label: t('Nome da Empresa') },
                { value: '[vaga]', label: t('Nome da Vaga') },
              ]}
              richEditorRules={[
                {
                  required:
                    (testTypeListValue && testTypeListValue.length !== 0) ||
                    subjectValue ||
                    fromNameValue ||
                    (attachmentsValue && attachmentsValue.length > 0),
                  message: t('Campo obrigatório'),
                },
                {
                  validator: (_, value: string) => {
                    if (
                      testTypeListValue &&
                      testTypeListValue.length === 0 &&
                      !subjectValue &&
                      !fromNameValue &&
                      !(attachmentsValue && attachmentsValue.length > 0)
                    ) {
                      return Promise.resolve();
                    }
                    if (
                      value &&
                      (value.includes('[link_testes]') || value.includes('[LINK_TESTS]'))
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t(
                          'Para enviar os testes é necessário incluir a variável "Link dos Testes" no corpo do e-mail.'
                        )
                      )
                    );
                  },
                },
              ]}
              templates={emailCommunicationTemplates.map((emailCommunicationTemplate) => ({
                id: emailCommunicationTemplate.id,
                name: emailCommunicationTemplate.name,
                message: emailCommunicationTemplate.message,
                subject: emailCommunicationTemplate.subject,
                fromName: emailCommunicationTemplate.from_name,
                attachments: emailCommunicationTemplate.attachments,
              }))}
              draft={
                !!(testTypeListValue && testTypeListValue.length === 0) &&
                !subjectValue &&
                !fromNameValue &&
                (!messageValue || messageValue === '<p><br></p>') &&
                !(attachmentsValue && attachmentsValue.length > 0)
              }
            />
          </Collapse.Panel>
        )}
      </Collapse>
    </>
  );
};

const stageSubTypeIconMap = {
  SCREENING_INTERVIEW: <UserOutlined />,
  HR_INTERVIEW: <TeamOutlined />,
  MANAGER_INTERVIEW: <CrownOutlined />,
  STAKEHOLDERS_INTERVIEW: <UsergroupAddOutlined />,
  PRACTICAL_TECHNICAL_CASE: <FileTextOutlined />,
  MINDSIGHT_TESTS: <BulbOutlined />,
  CUSTOM_TEST: <FormOutlined />,
  GROUP_DYNAMICS: <ClusterOutlined />,
  COMMUNICATION: <MessageOutlined />,
  OTHERS: <EllipsisOutlined />,
};

export default StageFormItem;
